import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { Survey, SurveyResponse, SurveyResponseItem } from '../../types/survey';

type Lang = 'JP' | 'EN';

// Maturity levels
type MaturityLevel = 1 | 2 | 3 | 4 | 5;
interface DimensionScore {
  score: MaturityLevel;
  answeredQuestions: number;
  totalQuestions: number;
  avgCompleteness: number;
  sampleAnswers: string[];
}

interface DimensionData {
  problemStatement: string[];
  keyQuestions: string[];
  keyRisks: string[];
  successCriteria: string[];
}

interface DimensionsContent {
  [dimension: string]: DimensionData;
}

const DIMENSIONS_JP: DimensionsContent = {
  'EA目標・スコープ': {
    problemStatement: [
      'EA導入の目的・スコープが不明確であり、全社的なIT投資判断に一貫性がない',
      '組織構造とEA推進体制の整合が取れておらず、意思決定のボトルネックが発生している',
      '期待効果やKPIが定義されていないため、EA活動の価値を定量的に示すことが困難',
    ],
    keyQuestions: [
      'EAを導入・推進する主な目的は何か？',
      'EAの対象スコープ（組織・領域）はどこまでか？',
      '期待する効果やKPIは定義されているか？',
      'EAに関わる主な組織・役割は明確か？',
      '最終的な意思決定者は誰か？',
    ],
    keyRisks: [
      'スコープが曖昧で全社展開が進まないリスク',
      '経営層のコミットメント不足によるEA活動の形骸化',
      'KPI未設定によるEA投資対効果の説明困難',
      '組織変更に伴うEA推進体制の崩壊',
    ],
    successCriteria: [
      'EA目的・スコープの全社合意が取れている',
      'KPIが定義され定期的に測定されている',
      'EA推進組織の役割と権限が明確に定義されている',
      '経営層による定期的なレビューが実施されている',
    ],
  },
  '現行業務・システム': {
    problemStatement: [
      '業務プロセスとシステムの対応関係が可視化されておらず、全体像の把握が困難',
      'システム間連携が個別最適で全体図が未整備、重複投資や連携障害が発生',
      'アプリケーション全体のTCOが把握されておらず、コスト最適化の判断ができない',
    ],
    keyQuestions: [
      '主要な業務プロセスは整理されているか？',
      'システム間連携の全体像は可視化されているか？',
      'アプリケーション全体のTCOは把握されているか？',
      'ベンダーロックインのリスクはどう管理されているか？',
      'セキュリティやガバナンス上の制約は何か？',
    ],
    keyRisks: [
      'システム全体像の未把握による重複投資の継続',
      'レガシーシステムの技術的負債の増大',
      'ベンダーロックインによる柔軟性の喪失',
      'セキュリティ標準の不統一によるインシデントリスク',
      'クラウド移行時のガバナンス空白',
    ],
    successCriteria: [
      '業務-システム対応マップが整備・維持されている',
      'システム連携全体図が可視化されている',
      'TCOの可視化とコスト最適化計画が策定されている',
      '技術標準・セキュリティ基準が統一されている',
    ],
  },
  'アーキテクチャレビュー': {
    problemStatement: [
      'アーキテクチャレビューが属人化しており、一貫した品質を担保できていない',
      'レビュープロセスがプロジェクト承認と連動しておらず、形式的になっている',
      'レビュー結果のフォローアップが不十分で、改善が継続されない',
    ],
    keyQuestions: [
      'アーキテクチャレビューはどのタイミングで実施されるか？',
      'レビューの主な観点は標準化されているか？',
      'レビュー結果はどのようにフォローアップされるか？',
      'EAレビューと他レビュー（セキュリティ等）との関係は？',
      'レビューの課題と改善の方向性は？',
    ],
    keyRisks: [
      'レビュー観点の属人化による品質のばらつき',
      'レビュー結果が反映されないことによる設計品質低下',
      'レビュープロセスの負荷による開発速度の低下',
      '形式的なレビューによるリスク見逃し',
    ],
    successCriteria: [
      'レビュー観点・チェックリストが標準化されている',
      'レビューがプロジェクトゲートと連動している',
      'レビュー結果の対応状況が追跡されている',
      'レビュープロセスのリードタイムが許容範囲内',
    ],
  },
  'ターゲットアーキテクチャ': {
    problemStatement: [
      'ターゲットアーキテクチャが定義されておらず、個別プロジェクトが独自に設計判断している',
      'ロードマップが不在で、段階的な移行計画が策定されていない',
      '現行と目標のギャップ分析ができておらず、優先順位付けが困難',
    ],
    keyQuestions: [
      'ターゲットアーキテクチャは定義されているか？',
      '現行からターゲットへのロードマップは策定されているか？',
      '移行計画の優先順位付けの基準は何か？',
      'ターゲットアーキテクチャの更新サイクルは？',
      '業界標準やベストプラクティスをどう取り込むか？',
    ],
    keyRisks: [
      '目標像の不在による個別最適の継続',
      '移行計画の不備による無計画な技術投資',
      '環境変化に追従できない硬直したアーキテクチャ',
      'ロードマップの形骸化による実行力の低下',
    ],
    successCriteria: [
      'ターゲットアーキテクチャが文書化・共有されている',
      '3-5年のロードマップが策定・更新されている',
      'ギャップ分析に基づく優先順位が明確',
      '定期的なアーキテクチャ更新プロセスがある',
    ],
  },
  'アーキテクチャガイド': {
    problemStatement: [
      'アーキテクチャガイドや設計標準が整備されておらず、設計品質にばらつきがある',
      '既存のガイドが陳腐化しており、最新技術や要件に対応できていない',
      'ガイドの浸透度が低く、プロジェクトチームが参照していない',
    ],
    keyQuestions: [
      'アーキテクチャガイドはどの範囲をカバーしているか？',
      'ガイドの更新頻度と管理プロセスは？',
      'ガイドの準拠状況はどう確認しているか？',
      '例外処理のルールは定義されているか？',
      'ガイドの教育・浸透施策はあるか？',
    ],
    keyRisks: [
      'ガイド未整備による設計品質の低下',
      '陳腐化したガイドによる技術的負債の増加',
      'ガイド未遵守による非標準アーキテクチャの増殖',
      '過度に厳格なガイドによるイノベーション阻害',
    ],
    successCriteria: [
      '包括的なアーキテクチャガイドが整備されている',
      '定期的なガイド更新プロセスが確立されている',
      'ガイド準拠率が目標値を達成している',
      'ガイドの例外処理プロセスが機能している',
    ],
  },
  '利用ツール・自動化': {
    problemStatement: [
      'EA管理に適切なツールが導入されておらず、手作業での管理に依存している',
      'ツール間の連携が不十分で、情報のサイロ化が発生している',
      'ツールの利用率が低く、投資効果が出ていない',
    ],
    keyQuestions: [
      'EA管理に利用しているツールは何か？',
      'ツール間のデータ連携はできているか？',
      'ツールの利用率と満足度はどの程度か？',
      '自動化されている作業と手作業の割合は？',
      'ツールの導入・運用コストは把握しているか？',
    ],
    keyRisks: [
      '手作業依存による情報の陳腐化と不整合',
      'ツールの乱立による管理コストの増大',
      '特定ツールへの依存によるベンダーロックイン',
      'ツール移行時のデータ損失リスク',
    ],
    successCriteria: [
      'EA管理に必要なツールが適切に選定・導入されている',
      'ツール間のデータ連携が自動化されている',
      'ツール利用率が80%以上を達成している',
      'ツールのROIが定量的に評価されている',
    ],
  },
};

const DIMENSIONS_EN: DimensionsContent = {
  'EA Goals & Scope': {
    problemStatement: [
      'EA objectives and scope are unclear, resulting in inconsistent IT investment decisions across the organization',
      'Misalignment between organizational structure and EA governance causes decision-making bottlenecks',
      'Lack of defined expected outcomes and KPIs makes it difficult to quantify EA value',
    ],
    keyQuestions: [
      'What is the primary purpose of EA adoption?',
      'What is the target scope (organization/domain)?',
      'Are expected outcomes and KPIs defined?',
      'Are EA-related roles and responsibilities clear?',
      'Who is the ultimate decision-maker?',
    ],
    keyRisks: [
      'Ambiguous scope preventing enterprise-wide adoption',
      'Lack of executive commitment leading to EA activity becoming superficial',
      'Inability to demonstrate EA ROI without defined KPIs',
      'EA governance collapse due to organizational changes',
    ],
    successCriteria: [
      'Enterprise-wide agreement on EA purpose and scope',
      'KPIs defined and measured regularly',
      'EA organization roles and authority clearly defined',
      'Regular executive-level reviews conducted',
    ],
  },
  'Current Systems': {
    problemStatement: [
      'Business process-to-system mapping is not visualized, making it difficult to understand the full picture',
      'System integration is locally optimized without an overall map, causing redundant investment',
      'Total application TCO is unknown, preventing cost optimization decisions',
    ],
    keyQuestions: [
      'Are major business processes documented?',
      'Is the system integration landscape visualized?',
      'Is total application TCO tracked?',
      'How is vendor lock-in risk managed?',
      'What are the security and governance constraints?',
    ],
    keyRisks: [
      'Continued redundant investment due to lack of system overview',
      'Growing technical debt from legacy systems',
      'Loss of flexibility due to vendor lock-in',
      'Security incident risk from inconsistent standards',
      'Governance gaps during cloud migration',
    ],
    successCriteria: [
      'Business-system mapping is maintained and current',
      'System integration landscape is visualized',
      'TCO visibility and cost optimization plan established',
      'Technical standards and security policies unified',
    ],
  },
  'Architecture Review': {
    problemStatement: [
      'Architecture reviews are person-dependent, unable to ensure consistent quality',
      'Review process is not linked to project approval, becoming ceremonial',
      'Insufficient follow-up on review findings, preventing continuous improvement',
    ],
    keyQuestions: [
      'At what timing are architecture reviews conducted?',
      'Are review criteria standardized?',
      'How are review findings followed up?',
      'What is the relationship with other reviews (security, etc.)?',
      'What are the current review challenges?',
    ],
    keyRisks: [
      'Quality variation due to person-dependent review criteria',
      'Design quality degradation when findings are not addressed',
      'Development speed reduction from review process overhead',
      'Risk oversight from ceremonial reviews',
    ],
    successCriteria: [
      'Review criteria and checklists are standardized',
      'Reviews are linked to project gates',
      'Finding resolution status is tracked',
      'Review process lead time is within acceptable range',
    ],
  },
  'Target Architecture': {
    problemStatement: [
      'No target architecture defined; individual projects make independent design decisions',
      'Absence of roadmap prevents staged migration planning',
      'Gap analysis between current and target state is incomplete, making prioritization difficult',
    ],
    keyQuestions: [
      'Is a target architecture defined?',
      'Is a roadmap from current to target state established?',
      'What are the prioritization criteria for migration?',
      'What is the target architecture update cycle?',
      'How are industry standards and best practices incorporated?',
    ],
    keyRisks: [
      'Continued local optimization without a target vision',
      'Unplanned technology investment without migration plan',
      'Rigid architecture that cannot adapt to environmental changes',
      'Roadmap becoming obsolete and losing execution power',
    ],
    successCriteria: [
      'Target architecture documented and shared',
      '3-5 year roadmap established and maintained',
      'Gap-analysis-based priorities are clear',
      'Regular architecture update process exists',
    ],
  },
  'Architecture Guide': {
    problemStatement: [
      'Architecture guides and design standards are not established, causing quality variation',
      'Existing guides are outdated and cannot address modern technologies',
      'Low guide adoption rate; project teams do not reference them',
    ],
    keyQuestions: [
      'What scope does the architecture guide cover?',
      'What is the guide update frequency and process?',
      'How is guide compliance verified?',
      'Are exception handling rules defined?',
      'What education/adoption measures exist?',
    ],
    keyRisks: [
      'Design quality degradation without established guides',
      'Increased technical debt from outdated guides',
      'Proliferation of non-standard architectures',
      'Innovation inhibition from overly strict guides',
    ],
    successCriteria: [
      'Comprehensive architecture guide is established',
      'Regular guide update process is in place',
      'Guide compliance rate meets target',
      'Exception handling process is functioning',
    ],
  },
  'Tools & Automation': {
    problemStatement: [
      'No appropriate EA management tools; relying on manual processes',
      'Insufficient tool integration causing information silos',
      'Low tool utilization rate; investment returns are not achieved',
    ],
    keyQuestions: [
      'What tools are used for EA management?',
      'Is data integration between tools achieved?',
      'What is the tool utilization and satisfaction rate?',
      'What is the ratio of automated vs. manual work?',
      'Are tool introduction/operation costs tracked?',
    ],
    keyRisks: [
      'Information staleness from manual dependency',
      'Increased management costs from tool proliferation',
      'Vendor lock-in from specific tool dependency',
      'Data loss risk during tool migration',
    ],
    successCriteria: [
      'Appropriate EA management tools selected and deployed',
      'Inter-tool data integration is automated',
      'Tool utilization rate exceeds 80%',
      'Tool ROI is quantitatively evaluated',
    ],
  },
};

const ROW_LABELS = {
  JP: { problemStatement: '課題定義', keyQuestions: '主要な質問', keyRisks: '主要なリスク', successCriteria: '成功基準' },
  EN: { problemStatement: 'Problem Statement', keyQuestions: 'Key Questions', keyRisks: 'Key Risks', successCriteria: 'Success Criteria' },
};

// Map question categories to dimensions (supports partial matching)
const CATEGORY_TO_DIMENSION: { [key: string]: string } = {
  // Japanese categories
  'EA目標': 'EA目標・スコープ',
  'スコープ': 'EA目標・スコープ',
  '期待効果': 'EA目標・スコープ',
  '組織構造': 'EA目標・スコープ',
  '現行業務': '現行業務・システム',
  '現行システム': '現行業務・システム',
  '業務プロセス': '現行業務・システム',
  'システム連携': '現行業務・システム',
  'TCO': '現行業務・システム',
  'アーキテクチャレビュー': 'アーキテクチャレビュー',
  'レビュー': 'アーキテクチャレビュー',
  'ターゲット': 'ターゲットアーキテクチャ',
  '将来像': 'ターゲットアーキテクチャ',
  'ロードマップ': 'ターゲットアーキテクチャ',
  '移行計画': 'ターゲットアーキテクチャ',
  'ガイド': 'アーキテクチャガイド',
  '設計標準': 'アーキテクチャガイド',
  '標準': 'アーキテクチャガイド',
  'ツール': '利用ツール・自動化',
  '自動化': '利用ツール・自動化',
  'EA管理': '利用ツール・自動化',
  // English categories
  'EA Goals': 'EA Goals & Scope',
  'Scope': 'EA Goals & Scope',
  'Current': 'Current Systems',
  'Systems': 'Current Systems',
  'Review': 'Architecture Review',
  'Target': 'Target Architecture',
  'Guide': 'Architecture Guide',
  'Tools': 'Tools & Automation',
  'Automation': 'Tools & Automation',
};

// Maturity level definitions
const MATURITY_LABELS = {
  JP: {
    1: { label: '初期', description: '定義なし・場当たり的' },
    2: { label: '反復', description: '一部定義・属人的' },
    3: { label: '定義済', description: '標準化・文書化' },
    4: { label: '管理', description: '測定・改善サイクル' },
    5: { label: '最適化', description: '継続的改善・業界標準' },
  },
  EN: {
    1: { label: 'Initial', description: 'Ad-hoc, undefined' },
    2: { label: 'Repeatable', description: 'Partially defined, person-dependent' },
    3: { label: 'Defined', description: 'Standardized, documented' },
    4: { label: 'Managed', description: 'Measured, improvement cycle' },
    5: { label: 'Optimized', description: 'Continuous improvement' },
  },
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MATURITY_COLORS: Record<MaturityLevel, string> = {
  1: '#e53e3e',
  2: '#dd6b20',
  3: '#d69e2e',
  4: '#38a169',
  5: '#2b6cb0',
};

const ROW_COLORS: Record<string, string> = {
  problemStatement: '#2c5282',
  keyQuestions: '#2b6cb0',
  keyRisks: '#c53030',
  successCriteria: '#38a169',
};

const DimensionsMapPage: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const [lang, setLang] = useState<Lang>('JP');
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (surveyId) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  const loadData = async () => {
    try {
      const data = await apiClient.getSurveyResults(surveyId!);
      setSurvey(data.survey);
      setResponses(data.results);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper: Map a category to a dimension
  const mapCategoryToDimension = (category: string, isJapanese: boolean): string | null => {
    const dims = isJapanese ? Object.keys(DIMENSIONS_JP) : Object.keys(DIMENSIONS_EN);
    // Direct match
    if (dims.includes(category)) return category;
    // Partial match via mapping
    for (const [key, dim] of Object.entries(CATEGORY_TO_DIMENSION)) {
      if (category.includes(key)) return dim;
    }
    return null;
  };

  // Calculate dimension scores based on user answers
  const dimensionScores = useMemo(() => {
    const isJapanese = lang === 'JP';
    const dims = isJapanese ? Object.keys(DIMENSIONS_JP) : Object.keys(DIMENSIONS_EN);
    const scores: Record<string, DimensionScore> = {};

    // Initialize scores
    dims.forEach(dim => {
      scores[dim] = {
        score: 1,
        answeredQuestions: 0,
        totalQuestions: 0,
        avgCompleteness: 0,
        sampleAnswers: [],
      };
    });

    // Aggregate all response items from all respondents
    const allItems: SurveyResponseItem[] = [];
    responses.forEach(r => {
      if (r.responses) allItems.push(...r.responses);
    });

    // Group by dimension
    const dimensionItems: Record<string, SurveyResponseItem[]> = {};
    dims.forEach(dim => { dimensionItems[dim] = []; });

    allItems.forEach(item => {
      const dim = mapCategoryToDimension(item.category || '', isJapanese);
      if (dim && dimensionItems[dim]) {
        dimensionItems[dim].push(item);
      }
    });

    // Calculate scores for each dimension
    dims.forEach(dim => {
      const items = dimensionItems[dim];
      if (items.length === 0) return;

      const totalQuestions = items.length;
      const answeredItems = items.filter(i => i.answer && i.answer.trim().length > 0);
      const answeredQuestions = answeredItems.length;

      // Calculate average answer length/completeness (as a proxy for quality)
      const avgLength = answeredItems.length > 0
        ? answeredItems.reduce((sum, i) => sum + (i.answer?.length || 0), 0) / answeredItems.length
        : 0;

      // Completeness ratio
      const completeness = totalQuestions > 0 ? answeredQuestions / totalQuestions : 0;

      // Calculate maturity score based on completeness and answer quality
      let score: MaturityLevel = 1;
      if (completeness >= 0.9 && avgLength >= 100) {
        score = 5;
      } else if (completeness >= 0.7 && avgLength >= 50) {
        score = 4;
      } else if (completeness >= 0.5 && avgLength >= 30) {
        score = 3;
      } else if (completeness >= 0.3 || avgLength >= 20) {
        score = 2;
      }

      // Get sample answers (up to 3)
      const sampleAnswers = answeredItems
        .map(i => i.answer || '')
        .filter(a => a.length > 10)
        .slice(0, 3);

      scores[dim] = {
        score,
        answeredQuestions,
        totalQuestions,
        avgCompleteness: Math.round(completeness * 100),
        sampleAnswers,
      };
    });

    return scores;
  }, [responses, lang]);

  // Calculate overall maturity score
  const overallScore = useMemo(() => {
    const dims = lang === 'JP' ? Object.keys(DIMENSIONS_JP) : Object.keys(DIMENSIONS_EN);
    const validScores = dims.map(d => dimensionScores[d]?.score || 1).filter(s => s > 0);
    if (validScores.length === 0) return 1;
    return Math.round(validScores.reduce((a, b) => a + b, 0) / validScores.length) as MaturityLevel;
  }, [dimensionScores, lang]);

  const [showEvaluation, setShowEvaluation] = useState(true);

  if (loading) return <div style={{ padding: '30px' }}>読み込み中...</div>;

  const dimensions = lang === 'JP' ? DIMENSIONS_JP : DIMENSIONS_EN;
  const labels = ROW_LABELS[lang];
  const dimensionKeys = Object.keys(dimensions);
  const maturityLabels = MATURITY_LABELS[lang];
  const rowKeys: (keyof DimensionData)[] = ['problemStatement', 'keyQuestions', 'keyRisks', 'successCriteria'];
  const title = lang === 'JP'
    ? `EA アセスメント ディメンションマッピング [課題, 質問, リスク, 成功基準]`
    : `EA Assessment Dimension Mapping [Problem, Questions, Risks, Success Criteria]`;
  const subtitle = survey ? survey.title : '';

  const respondentCount = responses.length;
  const completedCount = responses.filter(r => r.status === 'completed').length;

  return (
    <div style={{ padding: '24px', maxWidth: '100%', overflowX: 'auto', backgroundColor: '#f7fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <button
          onClick={() => navigate(`/admin/surveys/${surveyId}/results`)}
          style={{ padding: '6px 12px', backgroundColor: '#edf2f7', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#4a5568' }}
        >
          ← {lang === 'JP' ? '結果に戻る' : 'Back to Results'}
        </button>

        {/* JP/EN Toggle */}
        <div style={{ display: 'flex', border: '2px solid #2c5282', borderRadius: '6px', overflow: 'hidden' }}>
          <button
            onClick={() => setLang('JP')}
            style={{
              padding: '8px 20px',
              backgroundColor: lang === 'JP' ? '#2c5282' : 'white',
              color: lang === 'JP' ? 'white' : '#2c5282',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.9rem',
            }}
          >
            JP
          </button>
          <button
            onClick={() => setLang('EN')}
            style={{
              padding: '8px 20px',
              backgroundColor: lang === 'EN' ? '#2c5282' : 'white',
              color: lang === 'EN' ? 'white' : '#2c5282',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.9rem',
            }}
          >
            EN
          </button>
        </div>
      </div>

      {/* Title */}
      <h1 style={{ fontSize: '1.3rem', color: '#1a202c', marginBottom: '4px', fontWeight: 700 }}>{title}</h1>
      <p style={{ color: '#718096', fontSize: '0.85rem', marginBottom: '20px' }}>
        {subtitle} | {lang === 'JP' ? `回答者: ${respondentCount}名 (完了: ${completedCount}名)` : `Respondents: ${respondentCount} (Completed: ${completedCount})`}
      </p>

      {/* Evaluation Panel */}
      <div style={{ marginBottom: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <div
          onClick={() => setShowEvaluation(!showEvaluation)}
          style={{
            padding: '14px 20px',
            backgroundColor: '#2c5282',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontWeight: 700, fontSize: '1rem' }}>
              {lang === 'JP' ? '📊 成熟度評価' : '📊 Maturity Evaluation'}
            </span>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '4px 12px',
              backgroundColor: MATURITY_COLORS[overallScore],
              borderRadius: '20px',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}>
              <span>Lv.{overallScore}</span>
              <span>{maturityLabels[overallScore].label}</span>
            </div>
          </div>
          <span style={{ fontSize: '0.9rem' }}>{showEvaluation ? '▲' : '▼'}</span>
        </div>

        {showEvaluation && (
          <div style={{ padding: '20px' }}>
            {/* Maturity Legend */}
            <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {([1, 2, 3, 4, 5] as MaturityLevel[]).map(level => (
                <div key={level} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem' }}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '4px', backgroundColor: MATURITY_COLORS[level], display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '0.7rem' }}>
                    {level}
                  </div>
                  <span style={{ color: '#4a5568' }}>{maturityLabels[level].label}: {maturityLabels[level].description}</span>
                </div>
              ))}
            </div>

            {/* Dimension Score Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
              {dimensionKeys.map(dim => {
                const dimScore = dimensionScores[dim];
                if (!dimScore) return null;
                return (
                  <div key={dim} style={{
                    border: `2px solid ${MATURITY_COLORS[dimScore.score]}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      padding: '10px 14px',
                      backgroundColor: MATURITY_COLORS[dimScore.score],
                      color: 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{dim}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>Lv.{dimScore.score}</span>
                        <span style={{ fontSize: '0.75rem' }}>{maturityLabels[dimScore.score].label}</span>
                      </div>
                    </div>
                    <div style={{ padding: '12px 14px', fontSize: '0.8rem', color: '#4a5568' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>{lang === 'JP' ? '回答率' : 'Completion'}</span>
                        <span style={{ fontWeight: 600 }}>{dimScore.avgCompleteness}%</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>{lang === 'JP' ? '回答数' : 'Answered'}</span>
                        <span style={{ fontWeight: 600 }}>{dimScore.answeredQuestions} / {dimScore.totalQuestions}</span>
                      </div>
                      {/* Progress bar */}
                      <div style={{ height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${dimScore.avgCompleteness}%`,
                          height: '100%',
                          backgroundColor: MATURITY_COLORS[dimScore.score],
                          transition: 'width 0.3s',
                        }} />
                      </div>
                      {/* Sample answers preview */}
                      {dimScore.sampleAnswers.length > 0 && (
                        <div style={{ marginTop: '10px', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                          <div style={{ fontSize: '0.7rem', color: '#718096', marginBottom: '4px' }}>
                            {lang === 'JP' ? '回答サンプル:' : 'Sample answers:'}
                          </div>
                          {dimScore.sampleAnswers.slice(0, 2).map((ans, i) => (
                            <div key={i} style={{
                              fontSize: '0.72rem',
                              color: '#2d3748',
                              backgroundColor: '#f7fafc',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              marginBottom: '4px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {ans.length > 60 ? ans.substring(0, 60) + '...' : ans}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Overall Summary */}
            <div style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: '#f7fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
            }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#2d3748' }}>
                {lang === 'JP' ? '総合評価サマリー' : 'Overall Evaluation Summary'}
              </h4>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <div>
                  <span style={{ color: '#718096', fontSize: '0.8rem' }}>{lang === 'JP' ? '総合成熟度' : 'Overall Maturity'}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <span style={{ fontSize: '1.5rem', fontWeight: 700, color: MATURITY_COLORS[overallScore] }}>Lv.{overallScore}</span>
                    <span style={{ fontSize: '0.9rem', color: '#4a5568' }}>{maturityLabels[overallScore].label}</span>
                  </div>
                </div>
                <div>
                  <span style={{ color: '#718096', fontSize: '0.8rem' }}>{lang === 'JP' ? '評価対象' : 'Evaluated'}</span>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: '#2d3748', marginTop: '4px' }}>
                    {dimensionKeys.length} {lang === 'JP' ? 'ディメンション' : 'dimensions'}
                  </div>
                </div>
                <div>
                  <span style={{ color: '#718096', fontSize: '0.8rem' }}>{lang === 'JP' ? '回答者数' : 'Respondents'}</span>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: '#2d3748', marginTop: '4px' }}>
                    {completedCount} / {respondentCount}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Dimension Matrix Table */}
      <div style={{ overflowX: 'auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <table style={{ width: '100%', minWidth: '1200px', borderCollapse: 'collapse', backgroundColor: 'white' }}>
          <thead>
            <tr>
              <th style={{ width: '100px', padding: '14px 10px', backgroundColor: '#2c3e50', color: 'white', textAlign: 'center', fontSize: '0.8rem', position: 'sticky', left: 0, zIndex: 2 }}>
                {lang === 'JP' ? '分析軸' : 'Analysis'}
              </th>
              {dimensionKeys.map((dim) => (
                <th key={dim} style={{
                  padding: '14px 12px',
                  backgroundColor: '#3182ce',
                  color: 'white',
                  textAlign: 'center',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  borderLeft: '1px solid rgba(255,255,255,0.2)',
                  minWidth: '180px',
                }}>
                  {dim}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowKeys.map((rowKey) => (
              <tr key={rowKey}>
                <td style={{
                  padding: '12px 8px',
                  backgroundColor: ROW_COLORS[rowKey],
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  textAlign: 'center',
                  writingMode: 'vertical-rl' as any,
                  textOrientation: 'mixed' as any,
                  minHeight: '120px',
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  letterSpacing: lang === 'JP' ? '2px' : '0',
                }}>
                  {labels[rowKey]}
                </td>
                {dimensionKeys.map((dim) => {
                  const items = dimensions[dim][rowKey];
                  return (
                    <td key={dim} style={{
                      padding: '12px 14px',
                      borderBottom: '1px solid #e2e8f0',
                      borderLeft: '1px solid #e2e8f0',
                      verticalAlign: 'top',
                      fontSize: '0.78rem',
                      lineHeight: '1.5',
                      color: '#2d3748',
                    }}>
                      <ul style={{ margin: 0, paddingLeft: '14px' }}>
                        {items.map((item, i) => (
                          <li key={i} style={{ marginBottom: '6px' }}>{item}</li>
                        ))}
                      </ul>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Print/Export section */}
      <div style={{ marginTop: '20px', display: 'flex', gap: '8px' }}>
        <button
          onClick={() => {
            const w = window.open('', '_blank');
            if (!w) return;
            const tableEl = document.querySelector('table');
            if (!tableEl) return;
            w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
              <style>
                body { font-family: "Yu Gothic", "Meiryo", sans-serif; font-size: 10px; padding: 10px; }
                h1 { font-size: 14px; color: #1a202c; }
                table { width: 100%; border-collapse: collapse; }
                th, td { border: 1px solid #cbd5e0; padding: 6px 8px; text-align: left; vertical-align: top; }
                th { background: #3182ce; color: white; font-size: 11px; }
                ul { margin: 0; padding-left: 14px; }
                li { margin-bottom: 4px; }
                @media print { body { padding: 0; } .no-print { display: none; } }
              </style></head><body>
              <div class="no-print"><button onclick="window.print()" style="padding:8px 16px;background:#2c5282;color:white;border:none;border-radius:4px;cursor:pointer;margin-bottom:12px">PDF保存 / Print</button></div>
              <h1>${title}</h1>
              <p style="color:#718096;font-size:10px;margin-bottom:12px">${subtitle}</p>
              ${tableEl.outerHTML}
            </body></html>`);
            w.document.close();
          }}
          style={{ padding: '8px 16px', backgroundColor: '#c53030', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
        >
          PDF {lang === 'JP' ? '出力' : 'Export'}
        </button>
      </div>
    </div>
  );
};

export default DimensionsMapPage;
